import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "../components/layout/AppLayout";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";

type SubscriptionInfo = {
  subscription: { plan: string; status: string; current_period_end: string | null };
  credits: { interview_credits: number; report_credits: number };
};

export default function Settings() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState<SubscriptionInfo | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [switchingRole, setSwitchingRole] = useState(false);

  useEffect(() => {
    api.get<SubscriptionInfo>("/api/payments/subscription").then(setInfo).catch(() => undefined);
  }, []);

  async function handleLogout() {
    setLoggingOut(true);
    await logout();
    navigate("/");
  }

  async function handleSwitchRole() {
    if (!user) return;
    const targetRole = user.role === "company" ? "talent" : "company";
    setSwitchingRole(true);
    try {
      await api.post("/api/auth/role", { role: targetRole });
      await refresh();
      navigate("/onboarding");
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to switch role.");
    } finally {
      setSwitchingRole(false);
    }
  }

  return (
    <AppLayout title="Settings">
      <Card className="flex items-center justify-between gap-3 border border-slate-100">
        <div className="flex items-center gap-3">
          <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-full bg-slate-100">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt={user.name} className="h-full w-full object-cover" />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-lg font-semibold text-slate-400">
                {user?.name?.[0]}
              </span>
            )}
          </div>
          <div>
            <p className="text-sm font-semibold text-navy">{user?.name}</p>
            <p className="text-xs text-slate-400">{user?.email}</p>
            <span className="mt-1 inline-block rounded-full bg-slate-50 px-2.5 py-0.5 text-[10px] font-semibold text-navy border border-slate-200">
              {user?.role === "company" ? "🏢 Company / HR" : "👤 Talent / Jobseeker"}
            </span>
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleSwitchRole}
          loading={switchingRole}
          className="text-xs"
        >
          Switch to {user?.role === "company" ? "Talent" : "Company"} Role
        </Button>
      </Card>

      {info && (
        <Card className="mt-3 border border-slate-100">
          <p className="text-sm font-semibold text-navy">Subscription & Credits</p>
          <div className="mt-2 flex justify-between text-xs text-slate-500">
            <span>Plan</span>
            <span className="font-medium text-navy">{info.subscription.plan}</span>
          </div>
          <div className="mt-1 flex justify-between text-xs text-slate-500">
            <span>Status</span>
            <span className="font-medium text-navy">{info.subscription.status}</span>
          </div>
          <div className="mt-1 flex justify-between text-xs text-slate-500">
            <span>Interview Credits</span>
            <span className="font-medium text-navy">{info.credits.interview_credits}</span>
          </div>
          <div className="mt-1 flex justify-between text-xs text-slate-500">
            <span>Report Credits</span>
            <span className="font-medium text-navy">{info.credits.report_credits}</span>
          </div>
        </Card>
      )}

      <Button variant="outline" fullWidth className="mt-6" onClick={handleLogout} loading={loggingOut}>
        Log Out
      </Button>
    </AppLayout>
  );
}
